/*
 * <heritage-memo-modal>
 *
 * Whole-cart memo request modal. Sibling component to
 * heritage-reserve-modal, but the unit of work is the entire current
 * cart (N items) rather than a single PDP product.
 *
 * Responsibilities:
 *   1. Click interceptor on [data-memo-trigger] anchors / buttons.
 *      Dispatches `mdj:memo-open` so any surface can request the
 *      modal without needing a direct handle.
 *   2. On open: fetch /cart.js, render the items list, populate the
 *      contact summary from the server-rendered customer block.
 *   3. On confirm: submit the hidden Shopify contact form (the email
 *      to MDJ), add the request to MDJMemos (which also fires the
 *      optional memo webhook), clear the cart via /cart/clear.js,
 *      then swap to the success view.
 *
 * View states:
 *   confirm — signed-in partner with items in cart
 *   signin  — anonymous visitor
 *   success — submission accepted; cart cleared
 */

class HeritageMemoModal extends HTMLElement {
  constructor() {
    super();
    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleMemoOpen = this.handleMemoOpen.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleTrunkToggle = this.handleTrunkToggle.bind(this);
    this.handleDatesTbdToggle = this.handleDatesTbdToggle.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;

    // Portal to document.body so the fixed-position overlay escapes
    // the parent .shopify-section's stacking context (z-index: 2 in
    // base.css). The header group sits at z-index: 100 in the
    // document-level stack — without portaling, the modal renders
    // under the header. Same pattern as <heritage-gallery-lightbox>.
    // After re-parenting, connectedCallback fires again and the guard
    // above lets the real init run.
    if (document.body && this.parentNode !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this._initialized = true;

    this.overlayEl  = this.querySelector('[data-memo-modal-overlay]');
    this.closeEls   = this.querySelectorAll('[data-memo-modal-close]');
    this.submitBtn  = this.querySelector('[data-memo-submit]');
    this.itemsEl    = this.querySelector('[data-memo-items]');
    this.noteEl     = this.querySelector('[data-memo-note]');
    this.errorEl    = this.querySelector('[data-memo-error]');
    this.signinLink = this.querySelector('[data-memo-signin-link]');
    this.trunkToggle = this.querySelector('[data-memo-trunk-toggle]');
    this.datesTbdEl = this.querySelector('[data-memo-dates-tbd]');
    this.agreementEl = this.querySelector('[data-memo-agreement]');

    this.contactForm     = this.querySelector('#heritage-memo-contact-form');
    this.contactSubject  = this.querySelector('[data-memo-contact-subject]');
    this.contactName     = this.querySelector('[data-memo-contact-name]');
    this.contactEmail    = this.querySelector('[data-memo-contact-email]');
    this.contactPhone    = this.querySelector('[data-memo-contact-phone]');
    this.contactBody     = this.querySelector('[data-memo-contact-body]');

    this.isSignedIn  = this.dataset.isSignedIn === 'true';
    // Inside-sales reps (rdi_team tag, resolved server-side in
    // memo-modal.liquid) never see this partner-self form — every open
    // request is handed to <heritage-inside-sales-modal> instead. See
    // handleMemoOpen.
    this.isInsideRep = this.dataset.isInsideRep === 'true';
    this.loginUrl    = this.dataset.loginUrl || '/account/login';
    this.cartUrl     = this.dataset.cartUrl || '/cart';
    this.memosUrl    = this.dataset.memosUrl || '/pages/memo-requests';
    this.currency    = this.dataset.currency || 'USD';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    // Credit-app gate — now OPT-IN via the `memo_require_credit_app`
    // theme setting (data-require-credit-app), OFF by default. When ON,
    // a signed-in customer without the `credit_app` tag who tries to open
    // the modal — clicking a [data-memo-trigger] or landing on /cart with
    // ?intent=memo from the PDP — is routed to the credit application
    // instead. When OFF (default), any signed-in customer can request a
    // memo (MDJ doesn't ship without the credit app regardless, so the
    // request just acts as a follow-up trigger). The cart's Memo CTA is
    // gated by the same setting server-side.
    //
    // Tags arrive on window.__MDJ_INTEGRATIONS__.customer.tags as a
    // JS ARRAY — theme.liquid serializes them with `customer.tags |
    // json`, and Liquid's customer.tags is an array of strings. We
    // also tolerate a comma-separated string defensively in case a
    // future integration changes the shape.
    this.requireCreditApp = this.dataset.requireCreditApp === 'true';
    const cfg = window.__MDJ_INTEGRATIONS__ || {};
    const rawTags = (cfg.customer && cfg.customer.tags) || [];
    const tagList = Array.isArray(rawTags) ? rawTags : String(rawTags).split(',');
    this.hasCreditApp = tagList.some(function (t) {
      return String(t).trim().toLowerCase() === 'credit_app';
    });
    this.creditAppUrl = this.dataset.creditAppUrl || '/pages/become-a-partner';

    this.emailTemplates = this.readEmailTemplates();
    this.currentCart = null;

    this.closeEls.forEach((btn) => btn.addEventListener('click', this.handleClose));
    if (this.overlayEl) this.overlayEl.addEventListener('click', this.handleClose);
    if (this.submitBtn) this.submitBtn.addEventListener('click', this.handleSubmit);
    if (this.trunkToggle) this.trunkToggle.addEventListener('change', this.handleTrunkToggle);
    if (this.datesTbdEl) this.datesTbdEl.addEventListener('change', this.handleDatesTbdToggle);

    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('mdj:memo-open', this.handleMemoOpen);
    document.addEventListener('click', this.handleTriggerClick);

    // Auto-open the modal when the partner arrives at /cart with the
    // `?intent=memo` flag. The PDP Memo button sets that flag before
    // redirecting here (heritage-pdp-form.js), so a single tap on the
    // PDP carries the partner all the way into the memo submission
    // form. Strip the param via replaceState so a reload doesn't
    // re-trigger the modal.
    this.maybeAutoOpenFromIntent();
  }

  maybeAutoOpenFromIntent() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (params.get('intent') !== 'memo') return;
    params.delete('intent');
    const newQuery = params.toString();
    const cleanUrl = window.location.pathname + (newQuery ? '?' + newQuery : '') + window.location.hash;
    try { history.replaceState(null, '', cleanUrl); } catch (e) { /* noop */ }

    // Credit-app gate (opt-in). Only when memo_require_credit_app is ON:
    // a signed-in customer arriving from PDP with ?intent=memo but no
    // credit_app tag stays on /cart (server has already swapped the Memo
    // CTA to a credit-app redirect link + notice), rather than opening a
    // modal whose submit is gated. When the setting is OFF (default) this
    // is skipped and the modal auto-opens for any signed-in customer.
    if (this.requireCreditApp && this.isSignedIn && !this.hasCreditApp && !this.isInsideRep) return;

    // Defer one frame so any synchronous DOM/state set up by other
    // components on the cart page can finish before we open.
    requestAnimationFrame(() => this.handleMemoOpen());
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('mdj:memo-open', this.handleMemoOpen);
    document.removeEventListener('click', this.handleTriggerClick);
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest && event.target.closest('[data-memo-trigger]');
    if (!trigger) return;
    event.preventDefault();
    // Credit-app gate (opt-in) — same redirect path as the auto-open
    // guard above, and only active when memo_require_credit_app is ON.
    // OFF (default) → any signed-in customer's trigger opens the modal.
    // Reps are exempt: they book on a client's behalf.
    if (this.requireCreditApp && this.isSignedIn && !this.hasCreditApp && !this.isInsideRep) {
      window.location.href = this.creditAppUrl;
      return;
    }
    document.dispatchEvent(new CustomEvent('mdj:memo-open'));
  }

  async handleMemoOpen() {
    // Inside-sales rep → the inside-sales request form, not this one.
    // Single choke point: the cart's [data-memo-trigger], the PDP's
    // ?intent=memo auto-open, and any mdj:memo-open dispatch all arrive
    // here. The inside-sales modal is only rendered for reps, so if it's
    // somehow absent we fall through to the customer form rather than
    // leave the button dead.
    // whenDefined: on the ?intent=memo auto-open this can run before
    // heritage-inside-sales-memo.js (a later deferred script) has
    // registered its listener — dispatching early would be lost.
    if (this.isInsideRep && document.querySelector('heritage-inside-sales-modal')) {
      customElements.whenDefined('heritage-inside-sales-modal').then(() => {
        document.dispatchEvent(new CustomEvent('mdj:inside-sales-open'));
      });
      return;
    }

    if (!this.isSignedIn) {
      this.setState('signin');
      if (this.signinLink) {
        try {
          const u = new URL(this.loginUrl, location.origin);
          u.searchParams.set('checkout_url', this.cartUrl);
          this.signinLink.href = u.pathname + u.search;
        } catch (e) { /* fall back to plain login link */ }
      }
      this.open();
      return;
    }

    this.setState('confirm');
    // Fresh idempotency id per modal open = per submission intent. It's
    // reused across retries of this attempt (handleSubmit doesn't
    // regenerate it), so a double-click / lost-response retry dedupes at
    // JD; opening the modal again for a new request books a new memo.
    this.pendingMemoId = this.generateMemoId();
    if (this.noteEl) this.noteEl.value = '';
    this.resetPurpose();
    if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
    if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
    if (this.itemsEl) this.itemsEl.innerHTML = '<div class="heritage-memo-modal__items-loading">Loading cart…</div>';

    this.open();
    await this.refreshCart();
  }

  async refreshCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('cart-fetch-failed');
      const cart = await res.json();
      this.currentCart = cart;
      this.renderItems(cart);
    } catch (err) {
      this.currentCart = null;
      if (this.itemsEl) {
        this.itemsEl.innerHTML = '<div class="heritage-memo-modal__items-loading">Cart unavailable — please refresh and try again.</div>';
      }
    }
  }

  renderItems(cart) {
    if (!this.itemsEl) return;
    if (!cart || !cart.items || cart.items.length === 0) {
      this.itemsEl.innerHTML = '<div class="heritage-memo-modal__items-loading">Your cart is empty. Add pieces from the catalog before requesting memo.</div>';
      if (this.submitBtn) this.submitBtn.dataset.busy = 'true';
      return;
    }

    const rows = cart.items.map((item) => {
      const imgSrc = item.image ? item.image : '';
      const title = item.product_title || item.title || '';
      const rawSku = item.sku || (item.properties && item.properties.sku) || '';
      // Strip the configured display prefix (MDJ's "40") for the
      // partner-facing cart preview. Raw value flows through to
      // the email body and webhook payload unchanged below.
      const sku = window.MDJFormatSku ? window.MDJFormatSku(rawSku) : rawSku;
      const meta = [];
      if (sku) meta.push('No. ' + this.escapeHtml(sku));
      if (item.quantity && item.quantity !== 1) meta.push('Qty ' + item.quantity);
      const price = this.formatMoney(item.line_price);
      return (
        '<div class="heritage-memo-modal__item">' +
          '<div class="heritage-memo-modal__item-img"' + (imgSrc ? ' style="background-image: url(\'' + this.escapeAttr(imgSrc) + '\')"' : '') + '></div>' +
          '<div class="heritage-memo-modal__item-info">' +
            '<div class="heritage-memo-modal__item-name">' + this.escapeHtml(title) + '</div>' +
            (meta.length ? '<div class="heritage-memo-modal__item-meta">' + meta.join(' · ') + '</div>' : '') +
          '</div>' +
          '<div class="heritage-memo-modal__item-price">' + price + '</div>' +
        '</div>'
      );
    }).join('');

    const totalRow = (
      '<div class="heritage-memo-modal__items-total">' +
        '<span class="heritage-memo-modal__items-total-label">Cart subtotal</span>' +
        '<span class="heritage-memo-modal__items-total-value">' + this.formatMoney(cart.total_price) + '</span>' +
      '</div>'
    );

    this.itemsEl.innerHTML = rows + totalRow;
    if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
  }

  // Trunk Show / Special Event toggle — mirror the inside-sales modal:
  // flip data-trunk-open on the element so CSS reveals the event dates
  // and the 10% sell-through agreement. Clearing the acknowledgment when
  // toggled off keeps a stale acceptance from riding a non-trunk memo.
  handleTrunkToggle() {
    const on = !!(this.trunkToggle && this.trunkToggle.checked);
    this.dataset.trunkOpen = on ? 'true' : 'false';
    if (!on && this.agreementEl) this.agreementEl.checked = false;
  }

  // "Dates TBD" override — disable + clear the date inputs so a trunk-show
  // memo can go through without locked dates. The submit path sends empty
  // dates + tbd:true so JD/EasySoft can log the intent (non-blocking).
  handleDatesTbdToggle() {
    const tbd = !!(this.datesTbdEl && this.datesTbdEl.checked);
    ['start_event_date', 'end_event_date'].forEach((name) => {
      const el = this.querySelector('[data-memo-field="' + name + '"]');
      if (!el) return;
      el.disabled = tbd;
      if (tbd) el.value = '';
    });
  }

  resetPurpose() {
    this.querySelectorAll('[data-memo-purpose]').forEach((cb) => { cb.checked = false; });
    ['start_event_date', 'end_event_date'].forEach((name) => {
      const el = this.querySelector('[data-memo-field="' + name + '"]');
      if (el) { el.value = ''; el.disabled = false; }
    });
    if (this.datesTbdEl) this.datesTbdEl.checked = false;
    if (this.agreementEl) this.agreementEl.checked = false;
    this.dataset.trunkOpen = 'false';
  }

  async handleSubmit() {
    if (!this.currentCart || !this.currentCart.items || this.currentCart.items.length === 0) return;
    if (this.submitBtn && this.submitBtn.dataset.busy === 'true') return;
    if (this.submitBtn) this.submitBtn.dataset.busy = 'true';

    const cfg = window.__MDJ_INTEGRATIONS__ || {};
    const customer = cfg.customer || {};
    const addr = customer.default_address || {};
    const note = this.noteEl ? (this.noteEl.value || '').trim() : '';

    // Purpose of memo request + trunk-show event dates + sell-through
    // acknowledgment. The 10% agreement acknowledgment is REQUIRED to
    // submit a Trunk Show / Special Event memo.
    const purposes = Array.prototype.slice.call(this.querySelectorAll('[data-memo-purpose]'))
      .filter((cb) => cb.checked)
      .map((cb) => ({ key: cb.dataset.memoPurpose, label: cb.dataset.purposeText || '' }));
    const isTrunkShow = !!(this.trunkToggle && this.trunkToggle.checked);
    const datesTbd = !!(this.datesTbdEl && this.datesTbdEl.checked);
    const startEl = this.querySelector('[data-memo-field="start_event_date"]');
    const endEl = this.querySelector('[data-memo-field="end_event_date"]');
    const startVal = (startEl && startEl.value) || '';
    const endVal = (endEl && endEl.value) || '';
    // event_dates carries an explicit `tbd` flag so JD/EasySoft can tell an
    // intentional "not locked in yet" apart from missing data and log it as
    // the source of truth. Empty date strings when TBD.
    const eventDates = isTrunkShow
      ? { start_date: datesTbd ? '' : startVal, end_date: datesTbd ? '' : endVal, tbd: datesTbd }
      : null;
    const agreementAccepted = !!(this.agreementEl && this.agreementEl.checked);

    if (isTrunkShow && !agreementAccepted) {
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = 'Please accept the 10% sell-through agreement to request a Trunk Show / Special Event memo.';
      }
      if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
      return;
    }
    if (isTrunkShow && !datesTbd && (!startVal || !endVal)) {
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = 'Enter the trunk show start and end dates, or check "Dates TBD".';
      }
      if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
      return;
    }

    const now = new Date();

    const context = {
      customer_id: customer.id || null,
      email:       customer.email || '',
      first_name:  customer.first_name || '',
      last_name:   customer.last_name || '',
      phone:       addr.phone || customer.phone || '',
      company:     addr.company || '',
      address: {
        address1:      addr.address1 || '',
        address2:      addr.address2 || '',
        city:          addr.city || '',
        province:      addr.province || '',
        province_code: addr.province_code || '',
        zip:           addr.zip || '',
        country:       addr.country || '',
        country_code:  addr.country_code || ''
      },
      tags:               customer.tags || '',
      note:               note,
      requested_at:       now.toISOString(),
      requested_at_local: now.toLocaleString()
    };

    const memoItems = this.currentCart.items.map((item) => ({
      id:         item.product_id || item.id,
      variant_id: item.id,
      handle:     item.handle || '',
      title:      item.product_title || item.title || '',
      image:      item.image || '',
      sku:        item.sku || '',
      url:        item.url || (item.handle ? '/products/' + item.handle : ''),
      qty:        item.quantity || 1,
      line_price: item.line_price || 0,
      era:        (item.product && item.product.metafields && item.product.metafields.smadify && item.product.metafields.smadify.era) || ''
    }));

    // Unique id per submission attempt → the JD memo.id + Idempotency-Key.
    // Set once when the modal opened and reused for retries of this
    // attempt (so a double-click / lost-response retry dedupes at JD),
    // while a genuinely new request books a new memo. NOT the cart token:
    // that's stable for the cart's whole life, so JD deduped every
    // resubmit down to one-memo-per-cart — and it carries the cart's
    // ?key= secret. Lazy-init guards a submit that somehow predates open.
    if (!this.pendingMemoId) this.pendingMemoId = this.generateMemoId();

    const memoRequest = {
      id:           this.pendingMemoId,
      items:        memoItems,
      note:         note,
      total_price:  this.currentCart.total_price,
      currency:     this.currentCart.currency || this.currency,
      purposes:     purposes,
      eventDates:   eventDates,
      sellThroughAgreement: isTrunkShow ? { required: true, accepted: agreementAccepted, pct: 10 } : null
    };

    // Store locally + post to the JD intake. add() writes to the local
    // store first, so the request shows in the dashboard (and stays
    // resubmittable) even if the post fails — then resolves the real
    // accepted/failed result.
    let sent = false;
    try {
      if (window.MDJMemos) {
        sent = await window.MDJMemos.add(memoRequest, context);
      }
    } catch (err) {
      sent = false;
    }

    if (!sent) {
      // Didn't reach MDJ — keep the cart and don't claim success. The
      // partner can retry now, or resubmit later from their account
      // (the request is saved locally as a safety net).
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = 'We couldn’t submit your request just now — your cart is intact. Tap Confirm to try again, or resubmit it later from your account.';
      }
      if (this.submitBtn) this.submitBtn.dataset.busy = 'false';
      return;
    }

    // Accepted by MDJ — clear the cart, then swap to the success view.
    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).catch(() => { /* the request is committed even if the clear fails */ });

    // Tell the page (cart-form, etc.) the cart's empty so it can
    // refresh / re-render without a full reload.
    document.dispatchEvent(new CustomEvent('mdj:cart-cleared'));

    this.setState('success');
  }

  // DEPRECATED — no longer called. The memo notification now posts to the
  // JewelersDigital intake (via MDJMemos.add → MDJIntegrations.sendMemo),
  // which replaced this Shopify contact-form email (Shopify silently
  // dropped it on its CAPTCHA check). Kept temporarily for reference;
  // safe to remove along with the hidden contact form + email-template
  // <script> in sections/memo-modal.liquid.
  submitContactForm(context, memoRequest) {
    if (!this.contactForm) return;

    const itemsBlock = memoRequest.items.map((it, idx) => {
      const lines = [
        '  ' + (idx + 1) + '. ' + (it.title || '(untitled)'),
        '     Item No: ' + (it.sku || '—'),
        '     Qty: ' + (it.qty || 1) + '  ·  ' + this.formatMoney(it.line_price),
        '     Link: ' + (it.url ? (location.origin + it.url) : '—')
      ];
      return lines.join('\n');
    }).join('\n\n');

    const tokens = {
      '{partner_name}':    this.firstNonBlank([context.first_name, context.last_name].join(' ').trim(), context.email),
      '{partner_email}':   context.email,
      '{partner_phone}':   context.phone,
      '{partner_company}': context.company,
      '{partner_address}': this.formatAddress(context.address),
      '{item_count}':      memoRequest.items.length,
      '{items_block}':     itemsBlock,
      '{cart_total}':      this.formatMoney(memoRequest.total_price),
      '{note}':            context.note,
      '{requested_at}':    context.requested_at_local || context.requested_at,
      '{customer_id}':     context.customer_id == null ? '' : String(context.customer_id)
    };

    const subject = this.applyTokens(this.emailTemplates.subject, tokens);
    const body    = this.applyTokens(this.emailTemplates.body, tokens);

    if (this.contactSubject) this.contactSubject.value = subject;
    if (this.contactName)    this.contactName.value    = tokens['{partner_name}'] || '(no name)';
    if (this.contactEmail)   this.contactEmail.value   = context.email || this.dataset.fallbackEmail || '';
    if (this.contactPhone)   this.contactPhone.value   = tokens['{partner_phone}'] || '';
    if (this.contactBody)    this.contactBody.value    = body;

    try {
      fetch(this.contactForm.action, {
        method: 'POST',
        body: new FormData(this.contactForm),
        keepalive: true
      }).catch(() => { /* fire-and-forget */ });
    } catch (e) { /* fetch unavailable — degrade silently */ }
  }

  readEmailTemplates() {
    const el = this.querySelector('[data-memo-email-templates]');
    const fallback = {
      subject: 'Memo Request — {item_count} pieces from {partner_company}',
      body: 'Partner: {partner_name}\nEmail: {partner_email}\n\n{items_block}\n\nCart subtotal: {cart_total}\n\nNotes: {note}\n\nRequested: {requested_at}'
    };
    if (!el) return fallback;
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      return {
        subject: parsed.subject || fallback.subject,
        body: parsed.body || fallback.body
      };
    } catch (e) {
      return fallback;
    }
  }

  applyTokens(template, tokens) {
    if (!template) return '';
    let out = template;
    Object.keys(tokens).forEach((key) => {
      const value = tokens[key];
      const safe = (value == null || value === '') ? '—' : String(value);
      out = out.split(key).join(safe);
    });
    return out;
  }

  formatAddress(addr) {
    if (!addr) return '';
    const line1 = [addr.address1, addr.address2].filter(Boolean).join(' ');
    const cityRegion = [addr.city, addr.province_code || addr.province].filter(Boolean).join(', ');
    const tail = [cityRegion, addr.zip].filter(Boolean).join(' ');
    const country = addr.country_code || addr.country || '';
    return [line1, tail, country].filter(Boolean).join(', ');
  }

  // Unique idempotency id for a memo submission (mirrors MDJMemos.newId).
  // crypto.randomUUID where available; otherwise a random+time fallback.
  generateMemoId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'memo-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  firstNonBlank() {
    for (let i = 0; i < arguments.length; i++) {
      const v = arguments[i];
      if (v != null && String(v).trim() !== '') return v;
    }
    return '';
  }

  escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  escapeAttr(str) {
    return String(str || '').replace(/'/g, '%27');
  }

  setState(name) {
    this.dataset.state = name;
  }

  open() {
    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('heritage-overlay-open');
  }

  close() {
    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('heritage-overlay-open');
  }

  handleClose(event) {
    if (event) event.preventDefault();
    // If the partner just submitted, they're on the success view —
    // closing from there should reload the page so the cart refreshes
    // (especially relevant when they're standing on the /cart page).
    const wasSuccess = this.dataset.state === 'success';
    this.close();
    if (wasSuccess && /\/cart\b/.test(location.pathname)) {
      location.reload();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.dataset.open === 'true') this.close();
  }
}

if (!customElements.get('heritage-memo-modal')) {
  customElements.define('heritage-memo-modal', HeritageMemoModal);
}
